/**
 * Contratto puro per il documento "giorno" su Firestore.
 * Path: `{emailLocal}.magazzino` / calendario / giorni / `{dayId}`
 * Subcollection giorno: `voci-cassa`
 *
 * Usato per test e per allineare la creazione senza cambiare il modello dati.
 */

/** Placeholder array per campi lista ancora vuoti (storico app). */
export const EMPTY_LIST_PLACEHOLDER = ["false"];

/**
 * Campi del documento giorno appena creato (prima di eventuali voci-cassa).
 * Deve restare allineato a fetchDayTotal ramo `dExist === false`.
 */
export const buildEmptyDayDocumentFields = () => ({
  creditiGiorno: [...EMPTY_LIST_PLACEHOLDER],
  totaleCassa: 0,
  movimentiCassa: [...EMPTY_LIST_PLACEHOLDER],
  totaleMovimentiCassa: 0,
  totaleCreditiGiorno: 0,
  arraySpese: [...EMPTY_LIST_PLACEHOLDER],
  totaleSpese: 0,
  aggioGiornaliero: 0,
});

/**
 * Path logico del documento giorno (senza SDK).
 * @param {string} email
 * @param {string} dayId es. "6-9-2026"
 */
export const buildDayDocumentPath = (email, dayId) => {
  if (!email || typeof email !== "string" || !email.includes("@")) {
    throw new Error("DAY_PATH_INVALID_EMAIL");
  }
  if (!dayId || typeof dayId !== "string") {
    throw new Error("DAY_PATH_INVALID_DAY");
  }
  const localPart = email.split("@")[0];
  return {
    collectionRoot: `${localPart}.magazzino`,
    calendarioDoc: "calendario",
    giorniCollection: "giorni",
    dayId,
    fullPath: `${localPart}.magazzino/calendario/giorni/${dayId}`,
    vociCassaSubcollection: "voci-cassa",
    listaVociCassaCollection: "lista-voci-cassa",
  };
};

/**
 * ID giorno atteso dall'app: d-m-yyyy senza zeri iniziali (locale IT).
 * Non valida il calendario (29/2 ecc.), solo la forma usata come doc id.
 */
export const isValidDayIdFormat = (dayId) => {
  if (!dayId || typeof dayId !== "string") return false;
  const parts = dayId.split("-");
  if (parts.length !== 3) return false;
  const [d, m, y] = parts;
  if (!/^[1-9]\d*$/.test(d) || !/^[1-9]\d*$/.test(m) || !/^\d{4}$/.test(y)) {
    return false;
  }
  const day = Number(d);
  const month = Number(m);
  return day >= 1 && day <= 31 && month >= 1 && month <= 12;
};

/**
 * Payload di una voce-cassa clonata sul nuovo giorno (valore a 0).
 */
export const buildEmptyVoceCassaForDay = (voce) => ({
  alias: voce.alias,
  nome: voce.nome,
  valore: 0,
  percentualeQuadagno: Number(voce.percentualeQuadagno),
});

/**
 * Dopo un fetch async: applica il risultato solo se la data UI è ancora quella richiesta.
 * Se startDate Redux è ancora vuoto/undefined, applica (primo load).
 */
export const shouldApplyDayFetchResult = (currentStartDate, requestedDay) => {
  if (currentStartDate && currentStartDate !== requestedDay) {
    return false;
  }
  return true;
};

/**
 * Decide se creare un nuovo documento giorno.
 */
export const shouldCreateNewDayDocument = (exists) => exists === false;
